import DOMPurify from "dompurify";

/** Shared pure helpers for the ticket comments thread + composer. */

export const AGENT = "AGENT";

/**
 * Deep link to an agent's chat session, opened from the stage-history timeline and from a
 * mention-acknowledgement comment. Single source of truth for the format so both call sites stay in
 * sync.
 */
export const buildAgentChatUrl = (agentId, sessionId) =>
  `/agents/chats?agent=${encodeURIComponent(agentId)}&sessionId=${encodeURIComponent(sessionId)}`;

/**
 * Deep link to a team conversation (the orchestration parent session), used by stage history when a
 * run is owned by a team rather than a single agent. Optionally focuses a specific child worker
 * session. Matches the team viewer's `team`/`sessionId`/`focus` query params.
 */
export const buildTeamChatUrl = (teamId, parentSessionId, childSessionId) => {
  const base = `/agents/chats?team=${encodeURIComponent(teamId)}&sessionId=${encodeURIComponent(
    parentSessionId
  )}`;
  return childSessionId
    ? `${base}&focus=${encodeURIComponent(childSessionId)}`
    : base;
};

/**
 * Sanitise posted human comment HTML (TipTap output) for safe rendering, preserving any
 * {@code target}/{@code rel} the composer set. Agent mention acknowledgements are no longer stored
 * as HTML — they render client-side from the comment's {@code mentionAck}/{@code sessionId} fields —
 * so no chat-session link rewriting is needed here.
 */
export const sanitizeCommentHtml = (html) =>
  DOMPurify.sanitize(html, { ADD_ATTR: ["target", "rel"] });

/** Parse {@link TicketComment} createdAt to milliseconds for sorting (0 when missing/invalid). */
export const commentCreatedAtMs = (comment) => {
  const t = comment?.createdAt;
  if (!t) return 0;
  const ms = new Date(t).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

/** Newest {@link TicketComment} first. */
export const sortCommentsByCreatedDesc = (comments) =>
  [...comments].sort(
    (a, b) => commentCreatedAtMs(b) - commentCreatedAtMs(a)
  );

/** True when the stored body is TipTap/HTML markup (human composer), not plain markdown. */
/**
 * Detects bodies produced by the rich-text composer (TipTap wraps content in block tags) so the
 * thread renders them as HTML and leaves plain-text / markdown agent comments untouched.
 *
 * Backend twin: {@code HTML_BODY_PATTERN} in
 * {@code services/workflow-engine/.../board/service/BoardContextComposer.java} uses the same
 * block-tag set to decide HTML vs literal text for the agent digest. Keep the two tag lists in sync.
 */
export const isHtmlComment = (text) => {
  if (!text) return false;
  const trimmed = String(text).trim();
  return /<(?:p|div|br|ul|ol|li|strong|em|h[1-6]|blockquote|a|span)\b/i.test(
    trimmed
  );
};

/** Strips HTML to plain text (used to decide whether a rich-text body is empty). */
export const htmlToText = (html) => {
  if (!html) return "";
  return new DOMParser().parseFromString(html, "text/html").body.textContent || "";
};

/** Display name for a human comment author, falling back to their id. */
export const humanAuthorName = (author, users) => {
  if (!author?.id) return "Someone";
  const user = users.find((u) => u.email === author.id || u.userName === author.id);
  return user?.displayName || user?.userName || author.id;
};

/** Short single-line preview of a forwarded message. */
const snippet = (text, max = 120) => {
  const clean = String(text || "").replace(/\s+/g, " ").trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
};

/**
 * Derives the stage-history timeline events for comments that @-mentioned an agent and were
 * forwarded into that agent's session. The engine forwards the mention and, only on success,
 * threads an agent-authored acknowledgement (`mentionAck`) under the triggering comment — so each
 * ack is proof the message actually reached the agent. We render one event per ack, attributed to
 * the human author of the parent comment, sorted oldest first to interleave with stage runs.
 */
export const deriveCommentMentionEvents = ({
  comments = [],
  agents = [],
  users = [],
} = {}) => {
  const byId = new Map(comments.map((c) => [c.commentId, c]));
  const events = [];
  for (const ack of comments) {
    if (!ack?.mentionAck) continue;
    const agentId = ack.author?.id;
    if (!agentId || !ack.sessionId) continue;
    const parent = byId.get(ack.parentCommentId);
    const agent = agents.find((a) => a.agentId === agentId);
    events.push({
      id: ack.commentId,
      // Jump target is the human comment that triggered the forward.
      commentId: parent?.commentId || ack.parentCommentId,
      agentId,
      agentName: agent?.name || "Agent",
      sessionId: ack.sessionId,
      authorName: humanAuthorName(parent?.author, users),
      createdAt: parent?.createdAt || ack.createdAt,
      snippet: snippet(htmlToText(parent?.text).trim()),
    });
  }
  // commentCreatedAtMs reads `.createdAt`, which events also carry; it falls back to 0 for
  // missing/invalid timestamps so the comparator never sees NaN.
  return events.sort((a, b) => commentCreatedAtMs(a) - commentCreatedAtMs(b));
};

/** Friendly absolute timestamp, e.g. "June 11, 2026 at 7:10 AM". */
export const formatTimestamp = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const day = date.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const time = date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${day} at ${time}`;
};

const RELATIVE_UNITS = [
  { ms: 60_000, short: "m", long: "minute" },
  { ms: 3_600_000, short: "h", long: "hour" },
  { ms: 86_400_000, short: "d", long: "day" },
  { ms: 604_800_000, short: "w", long: "week" },
];

/** Compact relative time for thread headers, e.g. "2h", "3d", "Jun 10". */
export const formatRelativeTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const diffMs = Date.now() - date.getTime();
  if (diffMs < 45_000) return "now";

  for (const unit of RELATIVE_UNITS) {
    const amount = Math.floor(diffMs / unit.ms);
    if (amount < 1) continue;
    if (unit.long === "week" && amount >= 4) break;
    return `${amount}${unit.short}`;
  }

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
  });
};

export const formatSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
